import { apccCatalog } from './apccCatalog';

export const APCC_VARIABLES = {
  prec: { label: 'Precipitation', shortLabel: 'Rainfall', unit: 'mm/day anomaly', local: true, family: 'rainfall', scale: [-8, -4, -2, 0, 2, 4, 8] },
  t2m: { label: '2 m Air Temperature', shortLabel: 'Air temperature', unit: '°C anomaly', local: true, family: 'surface-temperature', scale: [-5, -3, -1, 0, 1, 3, 5] },
  sst: { label: 'Sea Surface Temperature', shortLabel: 'SST', unit: '°C anomaly', local: true, family: 'sea-surface-temperature', scale: [-5, -3, -1, 0, 1, 3, 5] },
  slp: { label: 'Sea-level Pressure', shortLabel: 'Sea-level pressure', unit: 'hPa anomaly', local: false, family: 'pressure', scale: [-5, -3, -1, 0, 1, 3, 5] },
  t850: { label: '850 hPa Air Temperature', shortLabel: '850 hPa temperature', unit: '°C anomaly', local: false, family: 'upper-atmosphere', scale: [-5, -3, -1, 0, 1, 3, 5] },
  z500: { label: '500 hPa Geopotential Height', shortLabel: '500 hPa height', unit: 'm anomaly', local: false, family: 'upper-atmosphere', scale: [-60, -40, -20, 0, 20, 40, 60] },
};

export const APCC_DATA_TYPES = ['forecast', 'hindcast'];
export const APCC_LEADS = ['3', '6'];
export const APCC_MODELS = ['deterministic', 'probabilistic'];
export const APCC_AGGREGATIONS = ['monthly', 'seasonal'];
export const APCC_MAP_MODES = ['gridded', 'voronoi', 'contour'];

export const APCC_PROBABILITY_PALETTES = {
  temperature: {
    below: ['#1400d3', '#2864ff', '#54affe', '#75d2fe', '#bdfffe'],
    near: ['#d2d2d2', '#a0a0a0', '#828282', '#646464', '#464646'],
    above: ['#ffeeb6', '#ffd18d', '#ff8e74', '#ff2b00', '#d6142e'],
  },
  precipitation: {
    below: ['#2f1201', '#662b01', '#9a6030', '#ce9b7c', '#e0c0ab'],
    near: ['#d2d2d2', '#a0a0a0', '#828282', '#646464', '#464646'],
    above: ['#b3fafe', '#66f0fe', '#2fe4ff', '#00aacd', '#007b9a'],
  },
};

export const APCC_DETERMINISTIC_PALETTES = {
  temperature: ['#1948a8', '#4d91d4', '#a9d7ee', '#f7f7f2', '#f4b183', '#df5a49', '#9e2131'],
  precipitation: ['#5b2d12', '#9a6030', '#d8af90', '#f7f7f2', '#9de8ef', '#20bcd3', '#006f8b'],
};

const APCC_ID = /^apcc\.(forecast|hindcast)\.(prec|t2m|sst|slp|t850|z500)\.(3|6)m\.(deterministic|probabilistic)$/;

export function parseApccId(value) {
  const legacy = value === '3.6.2'
    ? 'apcc.forecast.prec.6m.probabilistic'
    : (value === '2.4.2' || value === '7.1.4.2' ? 'apcc.forecast.sst.6m.probabilistic' : value);
  const match = String(legacy || '').match(APCC_ID);
  if (!match) return null;
  return { id: legacy, dataType: match[1], variable: match[2], lead: match[3], model: match[4] };
}

export function apccProductKey(parsed, aggregation) {
  if (!parsed) return null;
  return `${parsed.id}.${aggregation}`;
}

export function getApccProduct(parsed, aggregation) {
  return apccCatalog.products?.[apccProductKey(parsed, aggregation)] || null;
}

export function getApccRecords(parsed, aggregation, scope, mode) {
  const product = getApccProduct(parsed, aggregation);
  if (!product) return [];
  const folderScope = scope === 'indo-pacific' ? 'indo' : scope;
  return (product.records || []).filter(record => record.scope === folderScope && record.mode === mode);
}

export function getApccAvailableAggregations(parsed, scope, mode) {
  return APCC_AGGREGATIONS.filter(aggregation => getApccRecords(parsed, aggregation, scope, mode).length > 0);
}

export function apccVariableId(dataType, variable, lead, model) {
  return `apcc.${dataType}.${variable}.${lead}m.${model}`;
}

export function apccMenuChildren(variable) {
  return APCC_DATA_TYPES.map(dataType => ({
    label: dataType === 'forecast' ? 'Forecast' : 'Hindcast',
    key: `apcc.${variable}.${dataType}`,
    children: APCC_LEADS.map(lead => ({
      label: `${lead}-month lead`,
      key: `apcc.${variable}.${dataType}.${lead}m`,
      children: APCC_MODELS.map(model => ({
        label: model === 'deterministic' ? 'Deterministic ensemble' : 'Probabilistic terciles',
        key: apccVariableId(dataType, variable, lead, model),
      })),
    })),
  }));
}

export function apccSelectionLabel(parsed) {
  const variable = APCC_VARIABLES[parsed?.variable];
  if (!variable || !parsed) return 'APCC MME';
  const method = parsed.model === 'probabilistic' ? 'probabilistic terciles' : 'deterministic anomaly';
  return `${variable.label} · ${parsed.lead}-month · ${method}`;
}

export { apccCatalog };
